import type {
  PublicRoulette,
  PublicRouletteBet,
  PublicSeat,
  RouletteBetId,
  RouletteBetNum,
  RouletteInsideBetId,
  RoulettePocket,
  RouletteWheel,
} from '@tv-casino/shared';
import {
  ROULETTE_BETS,
  describeRouletteBet,
  insideAnchors,
  insideNumbers,
  pocketColor,
  wheelOrder,
} from '@tv-casino/shared';

/**
 * Turning published roulette state into a picture of the layout. Every stack
 * is public — bets sit face up on a real table — so the phone and the TV draw
 * the same chips from the same numbers, and only "which one is mine" differs.
 */

/**
 * One spot on the layout: a bet id plus, for straights and inside spreads,
 * its target. Outside bets take none, so their key is the id alone. String
 * keys keep the spot map flat, the way the craps felt keys bet ids.
 */
export type RouletteSpotKey = string;

export const rouletteSpotKey = (id: RouletteBetId, num?: RouletteBetNum): RouletteSpotKey =>
  `${id}:${num ?? ''}`;

/** The bet a key names, parsed back apart for the clear-one-stack event. */
export const rouletteSpotBet = (
  key: RouletteSpotKey,
): { id: RouletteBetId; num?: RouletteBetNum } => {
  const [id, raw] = key.split(':');
  if (raw === undefined || raw === '') return { id: id as RouletteBetId };
  // '00' is the pocket it is — parsing it as a number would fold it into 0.
  const num: RouletteBetNum = raw === '00' ? '00' : Number(raw);
  return { id: id as RouletteBetId, num };
};

export interface RouletteStack {
  seatIndex: number;
  amount: number;
  /** True for the phone holding this component: their chip is the pull target. */
  mine: boolean;
  name?: string | null;
}

export type RouletteStackMap = Partial<Record<RouletteSpotKey, RouletteStack[]>>;

const stackOf = (bet: PublicRouletteBet, seatIndex: number, mySeatIndex: number | null, name?: string | null): RouletteStack => ({
  seatIndex,
  amount: bet.amount,
  mine: seatIndex === mySeatIndex,
  name,
});

/** Every stake on the layout, grouped by spot and in seat order. */
export function rouletteStacks(
  roulette: PublicRoulette | null,
  seats: readonly PublicSeat[],
  mySeatIndex: number | null,
): RouletteStackMap {
  const out: RouletteStackMap = {};
  if (!roulette) return out;
  for (const rs of roulette.seats) {
    const seat = seats[rs.index] ?? null;
    for (const bet of rs.bets) {
      const key = rouletteSpotKey(bet.id, bet.num);
      (out[key] ??= []).push(stackOf(bet, rs.index, mySeatIndex, seat?.name));
    }
  }
  return out;
}

/** What the last roll did, spot by spot — for roulette, where the dolly sits. */
export type RouletteSpotMark = 'win';

/**
 * The spots the landed pocket pays, among the spots that actually carry
 * chips. A layout lights the winners the way a real table drops a dolly on
 * the number — it does not flood the rest of the felt with losing marks.
 */
export function rouletteWinMarks(
  pocket: RoulettePocket | null,
  stacks: RouletteStackMap,
): Partial<Record<RouletteSpotKey, RouletteSpotMark>> {
  const out: Partial<Record<RouletteSpotKey, RouletteSpotMark>> = {};
  if (pocket === null) return out;
  for (const key of Object.keys(stacks)) {
    const { id, num } = rouletteSpotBet(key);
    if (ROULETTE_BETS[id].covers(pocket, num)) out[key] = 'win';
  }
  return out;
}

/**
 * A pocket's ground and ink. The same three pairs `RouletteWheel.module.css`
 * and `RouletteTable.module.css` print: the felt's red, its black and the
 * zeroes' green. A native renderer has no stylesheet to read them out of, so
 * the values live here and the CSS keeps its own copy pinned by its tests —
 * one palette, two syntaxes.
 */
export const POCKET_TONES: Record<'red' | 'black' | 'green', { bg: string; ink: string }> = {
  red: { bg: '#a3282d', ink: '#fdeceb' },
  black: { bg: '#17232b', ink: '#eaf1f6' },
  green: { bg: '#1c6b41', ink: '#e7fbef' },
};

/** The tone a pocket wears; anything unknown reads black. */
export const pocketTone = (color: string): { bg: string; ink: string } =>
  POCKET_TONES[color as 'red' | 'black' | 'green'] ?? POCKET_TONES.black;

// --- the printed layout -----------------------------------------------------

/** One cell as the felt prints it: the bet it takes, its label, its colour. */
export interface RouletteSpot {
  key: RouletteSpotKey;
  betId: RouletteBetId;
  num?: RouletteBetNum;
  label: string;
  tone: 'red' | 'black' | 'green' | 'none';
}

/**
 * Which way the felt is printed. The TV has a room's width to spend, so it
 * prints the casino's own landscape layout; a phone held upright does not, so
 * it prints the same felt turned a quarter turn.
 */
export type RouletteLayout = 'wide' | 'tall';

/** The zeros the table actually has — '00' only exists on the American wheel. */
export const zerosFor = (wheel: RouletteWheel): RouletteSpot[] =>
  wheelOrder(wheel)
    .filter((pocket): pocket is 0 | '00' => pocket === 0 || pocket === '00')
    .map((pocket) => ({
      key: rouletteSpotKey('straight', pocket),
      betId: 'straight',
      num: pocket,
      label: String(pocket),
      tone: 'green',
    }));

/**
 * The thirty-six numbers in the order the layout prints them.
 *
 * `wide` is the felt as it stands in a casino: three rails of twelve reading
 * across, the top rail 3, 6, … 36. `tall` is that same felt turned a quarter
 * turn, the way every phone prints it — twelve rows of three reading 1, 2, 3
 * down to 34, 35, 36, so a thumb gets a cell it can actually hit.
 */
export const numberSpots = (layout: RouletteLayout = 'wide'): RouletteSpot[] => {
  const spots: RouletteSpot[] = [];
  const cell = (n: number): RouletteSpot => ({
    key: rouletteSpotKey('straight', n as RouletteBetNum),
    betId: 'straight',
    num: n as RouletteBetNum,
    label: String(n),
    tone: pocketColor(n),
  });
  if (layout === 'tall') {
    for (let n = 1; n <= 36; n += 1) spots.push(cell(n));
    return spots;
  }
  for (let row = 0; row < 3; row += 1) {
    for (let col = 1; col <= 12; col += 1) spots.push(cell(col * 3 - row));
  }
  return spots;
};

const flat = (cells: [RouletteBetId, string][]): RouletteSpot[] =>
  cells.map(([betId, label]) => ({
    key: rouletteSpotKey(betId),
    betId,
    label,
    tone: betId === 'red' ? 'red' : betId === 'black' ? 'black' : 'none',
  }));

/**
 * The 2-to-1 rail, top to bottom down the wide felt's right edge. Turned tall
 * it becomes a row beneath the numbers, so it runs the other way — each cell
 * then sits under the column it pays.
 */
export const columnSpots = (layout: RouletteLayout = 'wide'): RouletteSpot[] => {
  const cells: [RouletteBetId, string][] = [
    ['column3', '2 to 1'],
    ['column2', '2 to 1'],
    ['column1', '2 to 1'],
  ];
  return flat(layout === 'tall' ? [...cells].reverse() : cells);
};

export const dozenSpots = (): RouletteSpot[] =>
  flat([
    ['dozen1', '1st 12'],
    ['dozen2', '2nd 12'],
    ['dozen3', '3rd 12'],
  ]);

export const outsideSpots = (): RouletteSpot[] =>
  flat([
    ['low', '1–18'],
    ['even', 'Even'],
    ['red', 'Red'],
    ['black', 'Black'],
    ['odd', 'Odd'],
    ['high', '19–36'],
  ]);

// --- the spreads a phone offers ---------------------------------------------

/** The spreads, best odds first — a split beats a six line on a shared cell. */
export const ROULETTE_SPREADS: readonly RouletteInsideBetId[] = [
  'splitDown',
  'splitAcross',
  'street',
  'corner',
  'sixline',
];

/** One spread on offer: the bet it places, and the copy both screens print. */
export interface RouletteSpread {
  id: RouletteInsideBetId;
  anchor: number;
  /** "Split 5/8", "Street 4–6" — the cells it covers, named. */
  label: string;
  odds: string;
}

/**
 * Every spread that includes one number. A finger cannot reliably hit the line
 * between two cells on a phone, so neither layout asks it to: tap a number and
 * the spreads through it are offered as their own row, named by the cells they
 * cover. The web and the native felt read this one list, so a split is the
 * same bet under the same words on both.
 */
export const spreadsThrough = (num: number): RouletteSpread[] =>
  ROULETTE_SPREADS.flatMap((id) =>
    insideAnchors(id)
      .filter((anchor) => insideNumbers(id, anchor).includes(num))
      .map((anchor) => ({
        id,
        anchor,
        label: describeRouletteBet(id, anchor),
        odds: ROULETTE_BETS[id].odds,
      })),
  );

/**
 * How a settled stack reads once the pocket is out. A la-partage refund pays
 * back less than the stake, so the net is only ever quoted on a win — the
 * half-back case says so in words rather than printing a negative "profit".
 */
export const rouletteSettledLabel = (amount: number, payout: number): string => {
  if (payout === 0) return 'lost';
  if (payout > amount) return `+${payout - amount}`;
  return `${payout} back`;
};
